import { useQuery } from "@tanstack/react-query";
import * as AuthSession from "expo-auth-session";
import * as Linking from "expo-linking";
import { router } from "expo-router";
import { observer } from "mobx-react";
import React from "react";
import { Screen } from "~/components/Screen";
import { ButtonV2 } from "~/components/V2/Button";
import { ErrorViewV2 } from "~/components/V2/ErrorViewV2";
import { SpinnerV2 } from "~/components/V2/SpinnerV2";
import { View } from "~/components/V2/View";
import { environment } from "~/environment";
import { isIphone } from "~/utils/isIphone";
import type { ComponentType } from "react";

type AuthData = {
  discovery: AuthSession.DiscoveryDocument;
  request: Awaited<ReturnType<typeof AuthSession.loadAsync>>;
};

type WithAuthDataProps = {
  authRequest: AuthData["request"];
  discovery: AuthData["discovery"];
};

export function withAuthData<P extends object>(
  WrappedComponent: ComponentType<P & WithAuthDataProps>,
  redirectPath: string = "login",
) {
  return observer(function AuthDataWrapper(props: P) {
    const isIphoneDevice = isIphone();

    const redirectUri = Linking.createURL(redirectPath);

    // We pre-fetch the discovery document and generate the authentication request here,
    // before the user clicks the login button. This is done to avoid delays caused by
    // fetching the discovery document and generating the request, which can take some time.
    // On mobile web, if we attempt to open a popup after a delay, it may be blocked by
    // popup blockers. By pre-fetching, we ensure a smoother user experience and avoid
    // potential issues with popup blockers.
    const { data, isLoading, isError, error } = useQuery<AuthData>({
      queryKey: ["authData", redirectPath],
      queryFn: async () => {
        const discovery = await AuthSession.resolveDiscoveryAsync(
          environment.OIDC_DISCOVERY_URL,
        );

        let extraParams: Record<string, string> = {
          ui_locales: `${intl.locale}`, // Use the current locale for UI localization
        };

        if (environment.ENVIRONMENT_NAME !== "uat") {
          extraParams = {
            acr_values: "idp:sbid", //identity provider " swedish bank id
          };
        }

        if (isIphoneDevice) {
          // On iPhone, we use the "no_redirect" mode to avoid issues with the redirect flow.
          // The user is not redirected anywhere and is instructed to manually switch back to the app/browser they came from.
          // More acr_values can be found here: https://developer.signicat.com/identity-methods/sbid/integration-guide/oidc-sbid/
          if (extraParams.acr_values) {
            extraParams.acr_values += " sbid_app_ios_redirect_mode:no_redirect";
          } else {
            extraParams.acr_values = "sbid_app_ios_redirect_mode:no_redirect";
          }
        }

        const request = await AuthSession.loadAsync(
          {
            clientId: environment.OIDC_CLIENT_ID,
            usePKCE: true,
            redirectUri,
            scopes: environment.OIDC_SCOPES as string[],
            prompt: AuthSession.Prompt.Login,
            extraParams,
          },
          discovery,
        );

        return { discovery, request };
      },
    });

    if (isLoading) {
      return (
        <Screen withBottomInset withTopInset preventScroll>
          <View flex centerContent>
            <SpinnerV2 />
          </View>
        </Screen>
      );
    }

    if (isError) {
      return (
        <Screen withBottomInset withTopInset preventScroll>
          <View flex centerContent paddingHorizontal24>
            <ErrorViewV2
              error={error}
              title={intl.formatMessage({
                id: "z5tCw3",
                defaultMessage: "Something went wrong",
                description:
                  "Error message title when fetching active user fails",
              })}
            />
            <ButtonV2
              onPress={() => router.replace("/(auth)/login")}
              title={intl.formatMessage({
                id: "XDTgUs",
                defaultMessage: "Try Again",
                description: "Button that redirects to login screen",
              })}
              style={{ paddingHorizontal: 40 }}
            />
          </View>
        </Screen>
      );
    }

    if (!data) {
      return null; // Or a custom null state
    }

    return (
      <WrappedComponent
        {...props}
        authRequest={data.request}
        discovery={data.discovery}
      />
    );
  });
}
